import React, { createContext, useContext, useState, useCallback, useEffect, Suspense, useRef } from 'react';
import type { MessageNotification } from './MessageNotificationToast';
import { useAuth } from '@/providers/AuthProvider';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useNavigate } from 'react-router-dom';

const MessageNotificationToast = React.lazy(() =>
  import('./MessageNotificationToast').then((m) => ({ default: m.MessageNotificationToast }))
);

interface MessageNotificationContextType {
  addNotification: (notification: Omit<MessageNotification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const MessageNotificationContext = createContext<MessageNotificationContextType | undefined>(undefined);

export function useMessageNotifications() {
  const context = useContext(MessageNotificationContext);
  if (!context) {
    throw new Error('useMessageNotifications must be used within MessageNotificationProvider');
  }
  return context;
}

export function MessageNotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<MessageNotification[]>([]);
  const [listenerActive, setListenerActive] = useState(false);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const initialSnapshotHandledRef = useRef(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const MAX_NOTIFICATIONS = 5; // Maximum number of notifications to show at once

  useEffect(() => {
    if (listenerActive) return;

    const activate = () => setListenerActive(true);
    const activateWhenVisible = () => {
      if (document.visibilityState === 'visible') activate();
    };
    const timeoutId = window.setTimeout(activate, 1800);

    window.addEventListener('pointerdown', activate, { once: true });
    window.addEventListener('keydown', activate, { once: true });
    window.addEventListener('visibilitychange', activateWhenVisible);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('pointerdown', activate);
      window.removeEventListener('keydown', activate);
      window.removeEventListener('visibilitychange', activateWhenVisible);
    };
  }, [listenerActive]);

  // Listen for new messages in real-time
  useEffect(() => {
    if (!user?.uid || !listenerActive) return;

    processedMessageIdsRef.current = new Set();
    initialSnapshotHandledRef.current = false;

    // Query for messages where current user is a participant (more flexible)
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!initialSnapshotHandledRef.current) {
        snapshot.docs.forEach((docSnapshot) => {
          processedMessageIdsRef.current.add(docSnapshot.id);
        });
        initialSnapshotHandledRef.current = true;
        return;
      }

      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const messageId = change.doc.id;

          // Skip if we've already processed this message
          if (processedMessageIdsRef.current.has(messageId)) {
            return;
          }

          // Mark as processed
          processedMessageIdsRef.current.add(messageId);

          // Check if this is a new message (not from initial load)
          const messageTimestamp = data.createdAt?.toDate?.() || new Date();
          const timeSinceMessage = Date.now() - messageTimestamp.getTime();
          
          // Only show notification for messages from the last 30 seconds
          if (timeSinceMessage < 30000 && data.senderId !== user.uid) {
            const notification: Omit<MessageNotification, 'id'> = {
              messageId,
              senderId: data.senderId,
              senderName: data.senderName || 'Someone',
              senderPhoto: data.senderPhoto,
              content: data.content || 'Sent you a message',
              timestamp: messageTimestamp,
              seen: data.seen || false,
              conversationId: data.conversationId
            };

            addNotification(notification);

            // Play notification sound
            playNotificationSound();

            // Show browser notification if permission granted
            showBrowserNotification(notification);
          }
        }
      });
    }, (error) => {
      const errorCode = String((error as any)?.code || '').toLowerCase();
      if (!errorCode.includes('permission-denied')) {
        console.error('[MessageNotificationProvider] Error listening to messages:', error);
      }
    });

    return () => unsubscribe();
  }, [user?.uid, listenerActive]);

  const addNotification = useCallback((notification: Omit<MessageNotification, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: MessageNotification = { ...notification, id };

    setNotifications((prev) => {
      // Remove oldest if we've reached max
      const updated = prev.length >= MAX_NOTIFICATIONS ? prev.slice(1) : prev;
      return [...updated, newNotification];
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const playNotificationSound = () => {
    try {
      // Create a pleasant notification sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      oscillator.addEventListener('ended', () => {
        void audioContext.close();
      }, { once: true });
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  const showBrowserNotification = (notification: Omit<MessageNotification, 'id'>) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotif = new Notification(`New message from ${notification.senderName}`, {
        body: notification.content,
        icon: notification.senderPhoto || '/Logo.png',
        badge: '/Logo.png',
        tag: notification.messageId,
        requireInteraction: false,
        silent: false
      });

      browserNotif.onclick = () => {
        window.focus();
        navigate(`/messages?user=${notification.senderId}`);
        browserNotif.close();
      };

      setTimeout(() => browserNotif.close(), 8000);
    }
  };

  const handleReply = useCallback((notification: MessageNotification) => {
    navigate(`/messages?user=${notification.senderId}&reply=true`);
  }, [navigate]);

  return (
    <MessageNotificationContext.Provider value={{ addNotification, removeNotification, clearAll }}>
      {children}
      
      {/* Render notification toasts */}
      <Suspense fallback={null}>
        {notifications.map((notification, index) => (
          <MessageNotificationToast
            key={notification.id}
            notification={notification}
            position={index}
            onClose={() => removeNotification(notification.id)}
            onReply={() => handleReply(notification)}
          />
        ))}
      </Suspense>
    </MessageNotificationContext.Provider>
  );
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;

  return Notification.requestPermission();
}
